import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { usdCompact } from "../dashboard-selectors";
import type { WalletTypeItem } from "../dashboard-selectors";
import type { Wallet } from "@/types";

const TYPE_FILL: Record<string, string> = {
  Hot: "var(--ads-info)",
  Warm: "var(--ads-viz-amber)",
  Cold: "var(--ads-viz-green)",
  Sandbox: "var(--ads-viz-slate)",
  Pending: "var(--ads-pending)",
};

export function AumComposition({
  items,
  total,
  wallets,
}: {
  items: WalletTypeItem[];
  total: number;
  wallets: Wallet[];
}) {
  const topWallets = wallets
    .filter((w) => (w.balanceUsd ?? 0) > 0)
    .sort((a, b) => (b.balanceUsd ?? 0) - (a.balanceUsd ?? 0))
    .slice(0, 3);

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-[0.06em] text-muted">AUM Composition</h2>
          <div className="mt-0.5 text-xs text-muted">By wallet type · live wallet-store view</div>
        </div>
        <Link to="/wallets" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
          Wallets <ArrowUpRight className="size-3.5" />
        </Link>
      </div>

      <div className="flex flex-1 flex-col justify-between gap-4">
        <div className="flex flex-col gap-4">
          {items.map((item) => {
            const pct = (item.pct * 100).toFixed(1);
            const fill = TYPE_FILL[item.type] ?? "var(--ads-viz-slate)";
            return (
              <div key={item.type}>
                <div className="mb-1.5 flex items-center justify-between text-sm">
                  <span className="font-semibold text-ink">{item.type}</span>
                  <span className="font-mono text-xs text-muted">{usdCompact.format(item.value)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-disabled-soft">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: fill }} />
                  </div>
                  <div className="w-12 text-right text-sm font-bold text-ink">{pct}%</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-xl border border-line bg-bg p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-[0.05em] text-muted">Top wallets</div>
          <div className="space-y-2">
            {topWallets.map((w) => (
              <Link
                key={w.id}
                to={`/wallets/${w.id}`}
                className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-surface"
              >
                <div className="min-w-0">
                  <div className="truncate text-xs font-semibold text-ink">{w.name}</div>
                  <div className="text-[11px] text-muted">{w.chain}</div>
                </div>
                <div className="shrink-0 text-xs font-semibold text-ink">{usdCompact.format(w.balanceUsd ?? 0)}</div>
              </Link>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between rounded-xl bg-bg px-4 py-3">
          <div>
            <div className="text-xs text-muted">Total AUM</div>
            <div className="text-lg font-bold text-ink">{usdCompact.format(total)}</div>
          </div>
          <div className="text-xs text-muted">{items.length} wallet type{items.length === 1 ? "" : "s"}</div>
        </div>
      </div>
    </div>
  );
}
